import { zodResolver } from '@hookform/resolvers/zod'
import { type CreateAgencyInput, CreateAgencyInputSchema } from '@idc-forms/schemas'
import { Controller, useForm } from 'react-hook-form'
import { useCompaniesLookupQuery } from '@/features/companies/hooks/use-companies-query'
import { SidePanelActions } from '@/shared/components/side-panel/side-panel-actions'
import { Button } from '@/shared/ui/button'
import {
  Field,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
  FieldLegend,
  FieldSet
} from '@/shared/ui/field'
import { Input } from '@/shared/ui/input'
import { NativeSelect, NativeSelectOption } from '@/shared/ui/native-select'
import { Switch } from '@/shared/ui/switch'

type AgencyFormProps = {
  /** Valores iniciales (edición) — ausente en creación. */
  defaultValues?: Partial<CreateAgencyInput>
  submitLabel: string
  pending?: boolean
  onSubmit: (values: CreateAgencyInput) => void
  onCancel: () => void
}

/**
 * Formulario de agencia (crear/editar) para el side panel. RHF + zodResolver
 * con el MISMO schema (@idc-forms/schemas) que valida el backend. El código
 * es único POR EMPRESA y la predeterminada es una por empresa.
 */
export function AgencyForm({
  defaultValues,
  submitLabel,
  pending = false,
  onSubmit,
  onCancel
}: AgencyFormProps) {
  const {
    control,
    handleSubmit,
    formState: { errors }
  } = useForm<CreateAgencyInput>({
    resolver: zodResolver(CreateAgencyInputSchema),
    defaultValues: {
      default: false,
      active: true,
      ...defaultValues
    }
  })

  const { data: companies = [] } = useCompaniesLookupQuery()

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-5 px-3 py-3" noValidate>
      <FieldSet className="gap-4">
        <FieldLegend>Identificación</FieldLegend>
        <FieldGroup className="gap-4">
          <Controller
            control={control}
            name="company"
            render={({ field }) => (
              <Field data-invalid={Boolean(errors.company)}>
                <FieldLabel htmlFor="agency-company">Empresa</FieldLabel>
                <NativeSelect
                  id="agency-company"
                  value={field.value ?? ''}
                  onChange={(e) => {
                    field.onChange(e.target.value)
                  }}
                >
                  <NativeSelectOption value="" disabled>
                    Selecciona una empresa
                  </NativeSelectOption>
                  {companies.map((company) => (
                    <NativeSelectOption key={company._id} value={company._id}>
                      {company.name}
                    </NativeSelectOption>
                  ))}
                </NativeSelect>
                <FieldError errors={[errors.company]} />
              </Field>
            )}
          />

          <Controller
            control={control}
            name="name"
            render={({ field }) => (
              <Field data-invalid={Boolean(errors.name)}>
                <FieldLabel htmlFor="agency-name">Nombre</FieldLabel>
                <Input
                  id="agency-name"
                  value={field.value ?? ''}
                  onChange={field.onChange}
                  autoComplete="off"
                />
                <FieldError errors={[errors.name]} />
              </Field>
            )}
          />

          <Controller
            control={control}
            name="code"
            render={({ field }) => (
              <Field data-invalid={Boolean(errors.code)}>
                <FieldLabel htmlFor="agency-code">Código</FieldLabel>
                <Input
                  id="agency-code"
                  value={field.value ?? ''}
                  onChange={field.onChange}
                  autoComplete="off"
                />
                <FieldDescription>Único dentro de la empresa, p.ej. 01.</FieldDescription>
                <FieldError errors={[errors.code]} />
              </Field>
            )}
          />
        </FieldGroup>
      </FieldSet>

      <FieldSet className="gap-4">
        <FieldLegend>Contacto</FieldLegend>
        <FieldGroup className="gap-4">
          <Controller
            control={control}
            name="direction"
            render={({ field }) => (
              <Field data-invalid={Boolean(errors.direction)}>
                <FieldLabel htmlFor="agency-direction">Dirección</FieldLabel>
                <Input
                  id="agency-direction"
                  value={field.value ?? ''}
                  onChange={field.onChange}
                  autoComplete="off"
                />
                <FieldError errors={[errors.direction]} />
              </Field>
            )}
          />

          <Controller
            control={control}
            name="email"
            render={({ field }) => (
              <Field data-invalid={Boolean(errors.email)}>
                <FieldLabel htmlFor="agency-email">Correo electrónico</FieldLabel>
                <Input
                  id="agency-email"
                  type="email"
                  value={field.value ?? ''}
                  onChange={field.onChange}
                  autoComplete="off"
                />
                <FieldError errors={[errors.email]} />
              </Field>
            )}
          />

          <Controller
            control={control}
            name="phone"
            render={({ field }) => (
              <Field data-invalid={Boolean(errors.phone)}>
                <FieldLabel htmlFor="agency-phone">Teléfono</FieldLabel>
                <Input
                  id="agency-phone"
                  value={field.value ?? ''}
                  onChange={field.onChange}
                  autoComplete="off"
                />
                <FieldError errors={[errors.phone]} />
              </Field>
            )}
          />
        </FieldGroup>
      </FieldSet>

      <FieldSet className="gap-4">
        <FieldLegend>Estado</FieldLegend>
        <FieldGroup className="gap-4">
          <Controller
            control={control}
            name="default"
            render={({ field }) => (
              <Field orientation="horizontal">
                <FieldLabel htmlFor="agency-default">Predeterminada</FieldLabel>
                <Switch
                  id="agency-default"
                  checked={field.value ?? false}
                  onCheckedChange={field.onChange}
                />
              </Field>
            )}
          />
          <FieldDescription>
            Solo puede haber una agencia predeterminada por empresa: marcarla desmarca la actual y
            la deja activa.
          </FieldDescription>

          <Controller
            control={control}
            name="active"
            render={({ field }) => (
              <Field orientation="horizontal">
                <FieldLabel htmlFor="agency-active">Activa</FieldLabel>
                <Switch
                  id="agency-active"
                  checked={field.value ?? true}
                  onCheckedChange={field.onChange}
                />
              </Field>
            )}
          />
        </FieldGroup>
      </FieldSet>

      <SidePanelActions
        actions={[
          {
            label: submitLabel,
            variant: 'default',
            disabled: pending,
            primary: true,
            onClick: () => {
              void handleSubmit(onSubmit)()
            }
          },
          {
            label: 'Cancelar',
            variant: 'outline',
            disabled: pending,
            onClick: onCancel
          }
        ]}
      />
      {/* El submit real va por SidePanelActions; este botón invisible habilita Enter. */}
      <Button type="submit" className="hidden" disabled={pending}>
        {submitLabel}
      </Button>
    </form>
  )
}
